import { useState, useEffect } from 'react';
import api from '../services/api';
import '../styles/FormIntake.css';

function FormIntake() {
  const [currentStep, setCurrentStep] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);
  const [error, setError] = useState(null);
  const [eventName, setEventName] = useState('');
  const [workflowPath, setWorkflowPath] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    first_name: '',
    relationship_status: '',
    // Single path fields
    age: '',
    gender: '',
    seeking: '',
    preferred_age_range: '',
    interests: [],
    desired_vibe: '',
    contact_info: '',
    // Referral path fields
    friend_name: '',
    friend_age: '',
    friend_gender: '',
    friend_seeking: '',
    friend_interests: [],
    friend_contact: '',
    friend_vibe: ''
  });

  // Question configuration from questions.json
  const commonQuestions = [
    {
      id: 'name',
      label: "What's your first name?",
      type: 'text',
      field: 'first_name',
      required: true
    },
    {
      id: 'relationship_status',
      label: 'Quick question - are you single or taken?',
      type: 'single_choice',
      field: 'relationship_status',
      options: ['Single and looking 💕', 'Taken but want to help a friend 🤝'],
      required: true
    }
  ];

  const singlePathQuestions = [
    {
      id: 'age',
      label: 'How old are you?',
      type: 'number',
      field: 'age',
      required: true,
      min: 21,
      max: 99
    },
    {
      id: 'gender',
      label: 'How do you identify?',
      type: 'single_choice',
      field: 'gender',
      options: ['Man', 'Woman', 'Non-binary', 'Prefer not to say'],
      required: true
    },
    {
      id: 'seeking',
      label: 'Who are you looking to meet?',
      type: 'single_choice',
      field: 'seeking',
      options: ['Men', 'Women', 'Anyone'],
      required: true
    },
    {
      id: 'age_range',
      label: 'What age range are you interested in?',
      type: 'single_choice',
      field: 'preferred_age_range',
      options: ['21-25', '26-30', '31-35', '36-40', '40+', 'No preference'],
      required: true
    },
    {
      id: 'interests',
      label: 'What are you into? Pick 2-4 things!',
      type: 'multi_choice',
      field: 'interests',
      options: [
        'Outdoors & Adventure',
        'Live Music & Concerts',
        'Food & Wine',
        'Sports & Fitness',
        'Arts & Culture',
        'Travel',
        'Gaming',
        'Entrepreneurship'
      ],
      required: true,
      min: 2,
      max: 4
    },
    {
      id: 'vibe',
      label: 'In one sentence, what vibe are you looking for in someone?',
      type: 'text',
      field: 'desired_vibe',
      required: false,
      placeholder: "e.g., 'Someone adventurous who doesn't take life too seriously'"
    },
    {
      id: 'contact',
      label: 'How should we send you matches? (Email, phone, or Instagram)',
      type: 'text',
      field: 'contact_info',
      required: true
    }
  ];

  const referralPathQuestions = [
    {
      id: 'referral_intro',
      label: 'Pitch a single friend at a future event?',
      type: 'single_choice',
      field: 'referral_intro',
      options: ['Yes!', 'Not right now'],
      required: true
    },
    {
      id: 'pitcher_contact',
      label: "What's your best contact? (Phone, email, or Instagram) so we can send you updates on future events!",
      type: 'text',
      field: 'contact_info',
      required: true
    },
    {
      id: 'friend_name',
      label: "What's your friend's name?",
      type: 'text',
      field: 'friend_name',
      required: true
    },
    {
      id: 'friend_age',
      label: 'How old are they?',
      type: 'number',
      field: 'friend_age',
      required: true,
      min: 21,
      max: 99
    },
    {
      id: 'friend_gender',
      label: 'How do they identify?',
      type: 'single_choice',
      field: 'friend_gender',
      options: ['Man', 'Woman', 'Non-binary', 'Prefer not to say'],
      required: true
    },
    {
      id: 'friend_seeking',
      label: 'Who are they looking to meet?',
      type: 'single_choice',
      field: 'friend_seeking',
      options: ['Men', 'Women', 'Anyone'],
      required: true
    },
    {
      id: 'friend_interests',
      label: 'What are they into? Pick 2-4!',
      type: 'multi_choice',
      field: 'friend_interests',
      options: [
        'Outdoors & Adventure',
        'Live Music & Concerts',
        'Food & Wine',
        'Sports & Fitness',
        'Arts & Culture',
        'Travel',
        'Gaming',
        'Entrepreneurship'
      ],
      required: true,
      min: 2,
      max: 4
    },
    {
      id: 'friend_contact',
      label: "How can we reach them? (Phone, email, or Instagram - or just say 'skip')",
      type: 'text',
      field: 'friend_contact',
      required: false
    },
    {
      id: 'friend_vibe',
      label: 'Tell us about their vibe in one sentence!',
      type: 'text',
      field: 'friend_vibe',
      required: false
    }
  ];

  const getStepQuestion = () => {
    // First, show common questions
    if (currentStep < commonQuestions.length) {
      return commonQuestions[currentStep];
    }

    // Then show path-specific questions
    const pathQuestionIndex = currentStep - commonQuestions.length;

    if (workflowPath === 'single_path') {
      return singlePathQuestions[pathQuestionIndex];
    } else if (workflowPath === 'referral_path') {
      return referralPathQuestions[pathQuestionIndex];
    }

    return null;
  };

  const totalSteps = () => {
    if (!workflowPath) {
      return commonQuestions.length;
    }
    return commonQuestions.length +
      (workflowPath === 'single_path' ? singlePathQuestions.length : referralPathQuestions.length);
  };

  // Initialize session
  useEffect(() => {
    const initSession = async () => {
      try {
        // Clear old event IDs from previous events
        const storedEventId = localStorage.getItem('active-event-id');
        const validEventIds = ['west_side_bazaar_04_30']; // Add new events here as they come

        if (storedEventId && !validEventIds.includes(storedEventId)) {
          console.log('Clearing outdated event ID:', storedEventId);
          localStorage.removeItem('active-event-id');
          localStorage.removeItem('pitch-session-id'); // Clear old session too
        }

        const activeEventId = localStorage.getItem('active-event-id') || 'west_side_bazaar_04_30';

        if (!activeEventId) {
          setError('No active event. Please contact the event organizer.');
          setIsLoading(false);
          return;
        }

        // Check for existing session
        const existingSessionId = localStorage.getItem('pitch-session-id');

        if (existingSessionId) {
          try {
            const sessionData = await api.getFormSession(existingSessionId);
            if (!sessionData.completed) {
              setSessionId(existingSessionId);
              setEventName(sessionData.eventName || '');
              setIsLoading(false);
              return;
            } else {
              localStorage.removeItem('pitch-session-id');
            }
          } catch (err) {
            localStorage.removeItem('pitch-session-id');
          }
        }

        // Create new session
        const session = await api.createFormSession(activeEventId);
        setSessionId(session.sessionId);
        setEventName(session.eventName);
        localStorage.setItem('pitch-session-id', session.sessionId);
        setIsLoading(false);
      } catch (err) {
        console.error('Failed to initialize session:', err);
        setError('Failed to start. Please refresh the page.');
        setIsLoading(false);
      }
    };

    initSession();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMultiChoiceToggle = (field, option) => {
    const currentValues = formData[field] || [];
    const question = getStepQuestion();

    if (currentValues.includes(option)) {
      handleInputChange(field, currentValues.filter(v => v !== option));
    } else {
      if (currentValues.length < (question.max || 10)) {
        handleInputChange(field, [...currentValues, option]);
      }
    }
  };

  const isStepValid = () => {
    const question = getStepQuestion();
    if (!question) return false;

    const value = formData[question.field];

    if (question.required) {
      if (question.type === 'multi_choice') {
        return value && value.length >= (question.min || 1);
      }
      return value && value.toString().trim() !== '';
    }

    return true;
  };

  const handleNext = async () => {
    if (!isStepValid()) return;

    const question = getStepQuestion();

    // Check for branching logic at relationship_status
    if (question.id === 'relationship_status') {
      const path = formData.relationship_status.includes('Single') ? 'single_path' : 'referral_path';
      setWorkflowPath(path);

      // Update session with workflow path
      if (sessionId) {
        try {
          await api.updateFormResponse(sessionId, { workflow_path: path });
        } catch (err) {
          console.error('Failed to update workflow path:', err);
        }
      }
    }

    setCurrentStep(prev => prev + 1);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!sessionId) return;

    try {
      setIsLoading(true);

      // Prepare submission data based on workflow path
      const submissionData = {
        first_name: formData.first_name,
        relationship_status: formData.relationship_status.includes('Single') ? 'Single' : 'Taken',
        workflow_path: workflowPath
      };

      if (workflowPath === 'single_path') {
        submissionData.age = parseInt(formData.age);
        submissionData.gender = formData.gender;
        submissionData.seeking = formData.seeking;
        submissionData.preferred_age_range = formData.preferred_age_range;
        submissionData.interests = JSON.stringify(formData.interests);
        submissionData.desired_vibe = formData.desired_vibe || null;
        submissionData.contact_info = formData.contact_info;
      } else if (workflowPath === 'referral_path') {
        submissionData.contact_info = formData.contact_info;
        submissionData.referral_data = {
          friend_name: formData.friend_name,
          friend_age: parseInt(formData.friend_age),
          friend_gender: formData.friend_gender,
          friend_seeking: formData.friend_seeking,
          friend_interests: formData.friend_interests,
          friend_contact: formData.friend_contact || 'N/A',
          friend_vibe: formData.friend_vibe || null
        };
      }

      await api.submitFormResponse(sessionId, submissionData);

      setIsCompleted(true);
      localStorage.removeItem('pitch-session-id');
      setIsLoading(false);
    } catch (err) {
      console.error('Failed to submit form:', err);
      setError('Failed to submit. Please try again.');
      setIsLoading(false);
    }
  };

  const renderInput = (question) => {
    const value = formData[question.field];

    switch (question.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleInputChange(question.field, e.target.value)}
            placeholder={question.placeholder}
            className="form-input"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={(e) => handleInputChange(question.field, e.target.value)}
            min={question.min}
            max={question.max}
            className="form-input"
          />
        );

      case 'single_choice':
        return (
          <div className="button-options">
            {question.options.map((option) => (
              <button
                key={option}
                className={`option-button ${value === option ? 'selected' : ''}`}
                onClick={() => handleInputChange(question.field, option)}
              >
                {option}
              </button>
            ))}
          </div>
        );

      case 'multi_choice':
        return (
          <div className="button-options multi">
            {question.options.map((option) => (
              <button
                key={option}
                className={`option-button ${value?.includes(option) ? 'selected' : ''}`}
                onClick={() => handleMultiChoiceToggle(question.field, option)}
              >
                {option}
              </button>
            ))}
            <p className="multi-choice-hint">
              Selected: {value?.length || 0} / {question.max || 4}
              {question.min && ` (minimum ${question.min})`}
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="form-intake loading">
        <div className="loader">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="form-intake error">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className="form-intake completed">
        <div className="completion-message">
          <h2>🎉 You're all set{formData.first_name ? `, ${formData.first_name}` : ''}!</h2>
          {workflowPath === 'single_path' ? (
            <p>We'll be matching you with great people and sending updates soon!</p>
          ) : (
            <p>Thank you for signing up to pitch {formData.friend_name}! 🙌<br />
              We'll reach out to coordinate and get them connected with great people at a future event.</p>
          )}
        </div>
      </div>
    );
  }

  const question = getStepQuestion();

  // Calculate if this is the last step
  // IMPORTANT: Only consider it the last step if we have a workflow path set
  // AND we're on the actual last question
  const calculateIsLastStep = () => {
    if (!workflowPath) return false; // Can't be last step if path not chosen yet

    const pathQuestions = workflowPath === 'single_path'
      ? singlePathQuestions
      : referralPathQuestions;

    const totalQuestions = commonQuestions.length + pathQuestions.length;
    return currentStep === totalQuestions - 1;
  };

  const isLastStep = calculateIsLastStep();

  // Debug logging
  console.log('Current step:', currentStep);
  console.log('Workflow path:', workflowPath);
  console.log('Total steps:', totalSteps());
  console.log('Current question:', question);
  console.log('Is last step:', isLastStep);

  // If we don't have a question, something is wrong
  if (!question) {
    return (
      <div className="form-intake loading">
        <div className="loader">Loading question...</div>
      </div>
    );
  }

  return (
    <div className="form-intake">
      <div className="form-header">
        <img
          src="/assets/paf-buffalo-logo.png"
          alt="Pitch A Friend Logo"
          className="form-logo"
        />
        <h1>Pitch-A-Friend</h1>
        <p>{eventName || 'Event Sign Up'}</p>
      </div>

      <div className="progress-bar">
        <div
          className="progress-fill"
          style={{ width: `${((currentStep + 1) / totalSteps()) * 100}%` }}
        />
      </div>

      <div className="form-container">
        <div className="question-section">
          <h2>{question.label}</h2>
          {renderInput(question)}
        </div>

        <div className="form-navigation">
          {currentStep > 0 && (
            <button
              className="nav-button back"
              onClick={handleBack}
            >
              Back
            </button>
          )}

          {!isLastStep ? (
            <button
              className="nav-button next"
              onClick={handleNext}
              disabled={!isStepValid()}
            >
              Next
            </button>
          ) : (
            <button
              className="nav-button submit"
              onClick={handleSubmit}
              disabled={!isStepValid()}
            >
              Submit
            </button>
          )}
        </div>

        <div className="step-indicator">
          Step {currentStep + 1} of {totalSteps()}
        </div>
      </div>
    </div>
  );
}

export default FormIntake;
